import { useCallback, useEffect, useRef, useState } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { useNavigate } from 'react-router-dom';

// project imports
import MinimalLayout from 'layout/MinimalLayout';
import NavMotion from 'layout/NavMotion';
import AuthenticationLoading, { AUTHENTICATION_TIMEOUT_MS } from 'utils/AuthenticationLoading';
import ErrorPage from 'utils/ErrorPage';

const LoginRedirect = () => {
    const { keycloak, initialized } = useKeycloak();
    const navigate = useNavigate();
    const loginStarted = useRef(false);
    const loginAttemptRef = useRef(0);
    const loginTimeoutRef = useRef(null);
    const mountedRef = useRef(true);
    const [loginFailed, setLoginFailed] = useState(false);

    const clearLoginTimeout = useCallback(() => {
        if (!loginTimeoutRef.current) return;
        window.clearTimeout(loginTimeoutRef.current);
        loginTimeoutRef.current = null;
    }, []);

    const beginLogin = useCallback(async () => {
        const loginAttempt = loginAttemptRef.current + 1;

        loginAttemptRef.current = loginAttempt;
        loginStarted.current = true;
        clearLoginTimeout();
        if (mountedRef.current) setLoginFailed(false);

        loginTimeoutRef.current = window.setTimeout(() => {
            if (!mountedRef.current || loginAttempt !== loginAttemptRef.current) return;

            console.warn('[TMS authentication] Sign-in redirect timed out.');
            setLoginFailed(true);
        }, AUTHENTICATION_TIMEOUT_MS);

        try {
            await keycloak.login();
        } catch (error) {
            if (!mountedRef.current || loginAttempt !== loginAttemptRef.current) return;

            clearLoginTimeout();
            console.error('[TMS authentication] Sign-in redirect failed.', {
                name: error?.name || 'AuthenticationError'
            });
            setLoginFailed(true);
        }
    }, [clearLoginTimeout, keycloak]);

    useEffect(() => {
        mountedRef.current = true;

        return () => {
            mountedRef.current = false;
            loginAttemptRef.current += 1;
            clearLoginTimeout();
        };
    }, [clearLoginTimeout]);

    useEffect(() => {
        if (!initialized) return;

        if (keycloak.authenticated) {
            clearLoginTimeout();
            navigate('/dashboard/default', { replace: true });
            return;
        }

        if (!loginStarted.current) {
            beginLogin();
        }
    }, [beginLogin, clearLoginTimeout, initialized, keycloak.authenticated, navigate]);

    const handleRetry = () => {
        beginLogin();
    };

    if (loginFailed) {
        return (
            <ErrorPage
                status="authentication"
                onRetry={handleRetry}
                onSecondaryAction={() => window.location.reload()}
                secondaryActionLabel="Reload sign-in"
            />
        );
    }

    return <AuthenticationLoading primaryText="Opening secure sign-in…" progressLabel="Opening secure sign-in" />;
};

const LoginRoutes = {
    path: '/',
    element: (
        <NavMotion>
            <MinimalLayout />
        </NavMotion>
    ),
    children: [
        {
            path: '/',
            element: <LoginRedirect />
        }
    ]
};

export default LoginRoutes;
